import { useCallback, useEffect, useState } from "react";
import { fetchActorInvitePreview } from "../services/auth";
import { setStoredActorInviteAccountId } from "../utils/account-context";
import {
  applyAccountTheme,
  DEFAULT_ACCOUNT_THEME,
  seedActorAccountThemeFromInvite,
} from "../utils/account-branding-theme";

/**
 * Loads public account branding for actor invite routes (login / OTP verify).
 * @param {string|null} token Invite token from route params
 * @param {{ enabled?: boolean, applyTheme?: boolean }} options
 */
export const useActorInviteBranding = (token, options = {}) => {
  const enabled = options.enabled !== false && Boolean(token);
  const applyTheme = options.applyTheme !== false;
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setAccount(null);
      setIsLoading(false);
      setError(null);
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchActorInvitePreview(token);
      if (response?.error) {
        setError(response.error);
        return null;
      }

      const nextAccount = response?.account || null;
      setAccount(nextAccount);
      if (nextAccount) {
        if (applyTheme) {
          seedActorAccountThemeFromInvite(nextAccount);
        } else {
          if (nextAccount.id) {
            setStoredActorInviteAccountId(nextAccount.id);
          }
          applyAccountTheme(DEFAULT_ACCOUNT_THEME);
        }
      }
      return nextAccount;
    } catch (err) {
      setError(err?.message || "Failed to load invite branding");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, applyTheme]);

  useEffect(() => {
    if (enabled && !applyTheme) {
      applyAccountTheme(DEFAULT_ACCOUNT_THEME);
    }
  }, [enabled, applyTheme]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return undefined;
    }
    refresh();
    return undefined;
  }, [enabled, refresh]);

  return {
    account,
    isLoading,
    error,
    refresh,
  };
};

export default useActorInviteBranding;
